(() => {
  'use strict';
  const configNode = document.getElementById('JingleConfig');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }
  const root = `${config.root || '/'}`.replace(/\/?$/, '/');
  const endpoint = (value, fallback) => {
    const url = new URL(value || `${root}${fallback}`, location.origin);
    if (fallback.endsWith('.js') && !url.pathname.endsWith('.js')) url.pathname += '.js';
    return url.href;
  };
  const cartURL = endpoint(config.cartUrl, 'cart');
  const serviceDefinitions = {
    shipping_protection: { title: 'Shipping Protection', description: 'Added cover for lost or damaged parcels', detail: '', icon: 'shield' },
    priority: { title: 'Priority Processing', description: 'Priority processing & dispatch', detail: 'Standard delivery times apply', icon: 'truck' },
    warranty: { title: '1-Year Extended Warranty', description: 'Repair or replacement for manufacturing defects', detail: 'Excludes wear and misuse', icon: 'warranty' }
  };
  const configuredServices = Array.isArray(config.services) ? config.services : config.priorityVariantId ? [{ key: 'priority', variantId: config.priorityVariantId, price: config.priorityPrice, available: true }] : [];
  const services = configuredServices.filter(service => serviceDefinitions[service.key] && Number.isSafeInteger(Number(service.variantId)) && Number(service.variantId) > 0).map(service => ({ ...serviceDefinitions[service.key], ...service, variantId: String(service.variantId), available: service.available !== false }));
  const serviceFor = item => services.find(service => service.variantId === String(item.variant_id));
  const drawer = document.getElementById('JingleCartDrawer');
  const body = drawer?.querySelector('[data-cart-body]');
  const footer = drawer?.querySelector('[data-cart-footer]');
  const errorBox = drawer?.querySelector('[data-cart-error]');
  const status = document.getElementById('JingleCartStatus');
  const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const money = (cents, currency) => {
    try { return new Intl.NumberFormat(config.locale || 'en-US', { style: 'currency', currency: currency || config.currency || 'USD' }).format(Number(cents || 0) / 100); }
    catch { return `${(Number(cents || 0) / 100).toFixed(2)} ${currency || config.currency || 'USD'}`; }
  };
  const safeURL = (value, image = false) => {
    try {
      const url = new URL(value, location.origin);
      return ['http:', 'https:'].includes(url.protocol) && (image || url.origin === location.origin) ? url.href : cartURL;
    } catch { return cartURL; }
  };
  const serviceIcon = type => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">${type === 'truck' ? '<path d="M3 5h11v12H3M14 9h4l3 4v4h-7M1 9h7M1 13h5"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="18" r="2"/>' : type === 'warranty' ? '<path d="m12 2 7 3v7c0 4-7 9-7 9s-7-5-7-9V5l7-3Z"/><path d="m8 11 3 3 5-6"/>' : '<path d="m12 2 8 3v6c0 5-8 10-8 10S4 16 4 11V5l8-3Z"/><path d="M8 11h8m-4-4v8"/>'}</svg>`;
  const kitOffers = config.bundleUpgrades || (config.bundleUpgrade ? [config.bundleUpgrade] : []);
  let upgraded = null;
  let busy = false;
  let opener = null;
  let currentCart = null;
  let cartVerified = false;
  let loadVersion = 0;
  let cleanPromise = null;
  const announce = text => { if (status) { status.textContent = ''; requestAnimationFrame(() => { status.textContent = text; }); } };
  const showError = text => {
    if (errorBox) { errorBox.textContent = text; errorBox.hidden = !text; }
    const refresh = drawer?.querySelector('[data-cart-refresh]');
    if (refresh) refresh.hidden = !text;
  };
  const request = async (url, options = {}) => {
    const controller = new AbortController();
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal, headers: { Accept: 'application/json', ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }), ...options.headers } });
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(typeof data.description === 'string' ? data.description : 'This item could not be updated. Please check the quantity and try again.');
        error.serverResponse = true;
        error.uncertain = response.status >= 500;
        throw error;
      }
      return data;
    } catch (error) {
      if (error.serverResponse) throw error;
      const failure = new Error(timedOut ? 'That took longer than expected. Please check your bag before trying again.' : 'We could not confirm the update. Please check your connection and your bag before trying again.');
      // Aborting the browser request does not undo a server-side cart mutation.
      failure.uncertain = !!options.method && options.method.toUpperCase() !== 'GET';
      throw failure;
    } finally { clearTimeout(timeout); }
  };
  const hasPhysicalItems = cart => cart.items.some(item => !serviceFor(item) && item.requires_shipping);
  const canCheckout = () => cartVerified && !!currentCart?.items.length && (!currentCart.items.some(serviceFor) || hasPhysicalItems(currentCart));
  const syncCheckout = () => drawer?.querySelectorAll('[name="checkout"]').forEach(button => { button.disabled = busy || !canCheckout(); });
  // Each optional service is one per order; services cannot remain on their own.
  const cleanServices = async cart => {
    const serviceLines = cart.items.filter(serviceFor);
    if (!serviceLines.length) return cart;
    const eligible = hasPhysicalItems(cart);
    const updates = {};
    const seen = new Set();
    serviceLines.forEach(item => {
      const id = String(item.variant_id);
      const quantity = eligible && !seen.has(id) ? 1 : 0;
      seen.add(id);
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    if (!Object.keys(updates).length) return cart;
    if (cleanPromise) return cleanPromise;
    cleanPromise = request(`${root}cart/update.js`, { method: 'POST', body: JSON.stringify({ updates }) });
    try { return await cleanPromise; } finally { cleanPromise = null; }
  };
  const setBusy = value => {
    busy = value;
    drawer?.setAttribute('aria-busy', String(value));
    document.querySelectorAll('[data-product-form] button[type="submit"], [data-product-form] input[type="submit"], [data-full-service-form] button[type="submit"], #JingleCartDrawer [data-cart-action], #JingleCartDrawer [name="checkout"]').forEach(control => {
      if (value) {
        if (control.dataset.jingleDisabled === undefined) control.dataset.jingleDisabled = String(control.disabled);
        control.disabled = true;
      } else if (control.dataset.jingleDisabled !== undefined) {
        control.disabled = control.dataset.jingleDisabled === 'true';
        delete control.dataset.jingleDisabled;
      }
    });
    syncCheckout();
  };
  const render = cart => {
    currentCart = cart;
    cartVerified = true;
    const refresh = drawer?.querySelector('[data-cart-refresh]');
    if (refresh) refresh.hidden = true;
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = cart.item_count; node.hidden = cart.item_count === 0; });
    drawer?.querySelectorAll('[data-drawer-count]').forEach(node => { node.textContent = cart.item_count ? `(${cart.item_count})` : ''; });
    if (!body) return;
    const price = cents => money(cents, cart.currency);
    if (!cart.items.length) {
      body.innerHTML = '<div class="jingle-cart-empty"><h3>Your bag is empty</h3><p>Choose your CHROMEBURN kit.</p><button type="button" class="jingle-cart-secondary" data-cart-close>Shop CHROMEBURN</button></div>';
      if (footer) footer.hidden = true;
      syncCheckout();
      return;
    }
    const displayItems = [...cart.items].sort((a, b) => Number(!!serviceFor(a)) - Number(!!serviceFor(b)));
    body.innerHTML = displayItems.map((item, index) => {
      const service = serviceFor(item);
      const designOffer = kitOffers.find(offer => offer.productId === Number(item.product_id));
      const name = service ? service.title : designOffer ? `CHROMEBURN ${designOffer.design}` : item.product_title || item.title;
      const image = designOffer?.design === 'Fire Wings' ? designOffer.image : item.featured_image?.url || item.image;
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title.replace(/^Red \/ /, '') : '';
      const properties = Object.entries(item.properties || {}).filter(([key, value]) => !key.startsWith('_') && value !== '' && value != null);
      const discounts = (item.line_level_discount_allocations || []).map(discount => `<span>${escapeHTML(discount.discount_application?.title || 'Discount')} −${price(discount.amount)}</span>`).join('');
      const unitPrice = item.unit_price_measurement ? `<small>${price(item.unit_price)} / ${escapeHTML(item.unit_price_measurement.reference_value === 1 ? '' : item.unit_price_measurement.reference_value)}${escapeHTML(item.unit_price_measurement.reference_unit)}</small>` : '';
      return `<article class="jingle-cart-line" data-line-key="${escapeHTML(item.key)}">
        <div class="jingle-cart-image">${service ? serviceIcon(service.icon) : image ? `<img src="${escapeHTML(safeURL(image, true))}" width="88" height="100" loading="lazy" alt="${escapeHTML(name)}">` : ''}</div>
        <div class="jingle-cart-line-info">${service ? `<h3>${escapeHTML(name)}</h3>` : `<h3><a href="${escapeHTML(safeURL(item.url))}">${escapeHTML(name)}</a></h3>`}
          ${variant && !service ? `<p>${escapeHTML(variant)}</p>` : ''}
          ${properties.map(([key, value]) => `<p>${escapeHTML(key)}: ${escapeHTML(value)}</p>`).join('')}
          ${item.selling_plan_allocation ? `<p>${escapeHTML(item.selling_plan_allocation.selling_plan.name)}</p>` : ''}
          <div class="jingle-cart-line-price">${item.original_line_price > item.final_line_price ? `<s>${price(item.original_line_price)}</s> ` : ''}<strong>${price(item.final_line_price)}</strong>${unitPrice}</div>
          ${discounts ? `<div class="jingle-cart-discounts">${discounts}</div>` : ''}
          <div class="jingle-cart-line-controls">${service ? '<span class="jingle-cart-once">One per order</span>' : `<div class="jingle-cart-quantity"><button type="button" data-cart-action data-cart-step="-1" aria-label="Decrease quantity of ${escapeHTML(name)}">−</button><label class="jingle-cart-sr" for="JingleQty-${index}">Quantity of ${escapeHTML(name)}</label><input id="JingleQty-${index}" type="number" value="${item.quantity}" min="0" step="${item.quantity_rule?.increment || 1}" inputmode="numeric" data-cart-action data-cart-quantity><button type="button" data-cart-action data-cart-step="1" aria-label="Increase quantity of ${escapeHTML(name)}">+</button></div>`}<button type="button" class="jingle-cart-remove" data-cart-action data-cart-remove aria-label="Remove ${escapeHTML(name)}">Remove</button></div>
        </div></article>`;
    }).join('');
    const upgrade = window.HwOffers?.findUpgrade(cart, kitOffers, config.currency);
    if (upgrade) {
      body.insertAdjacentHTML('beforeend', `<section class="hw-cart-upgrade" aria-label="Add a second kit"><img src="${escapeHTML(safeURL(upgrade.offer.image, true))}" width="58" height="66" alt="CHROMEBURN complete projector kit"><div><h3>One for your riding buddy?</h3><p>2 ${escapeHTML(upgrade.offer.design)} kits for ${price(upgrade.offer.toPrice)} total.</p></div><button type="button" data-cart-action data-kit-upgrade="${upgrade.offer.productId}">Add a second kit · +${price(upgrade.difference)}</button></section>`);
    } else if (upgraded && cart.items.some(item => Number(item.variant_id) === upgraded)) {
      body.insertAdjacentHTML('beforeend', '<div class="hw-cart-confirm" tabindex="-1" data-upgrade-confirm>Added — your bag now has 2 complete kits.</div>');
    }
    const offeredServices = hasPhysicalItems(cart) ? services.filter(service => (service.available && Number.isFinite(Number(service.price)) && Number(service.price) >= 0) || cart.items.some(item => service.variantId === String(item.variant_id))) : [];
    if (offeredServices.length) {
      body.insertAdjacentHTML('beforeend', `<div class="jingle-cart-services"><h3 class="jingle-cart-services-heading">Optional extras</h3>${offeredServices.map(service => {
        const selected = cart.items.find(item => service.variantId === String(item.variant_id));
        return `<section class="jingle-cart-priority${selected ? ' is-added' : ''}" aria-label="${escapeHTML(service.title)}"><div class="jingle-cart-priority-icon">${serviceIcon(service.icon)}</div><div><h3>${escapeHTML(service.title)}</h3><p>${escapeHTML(service.description)}${service.detail ? `<br><span>${escapeHTML(service.detail)}</span>` : ''}</p></div><div class="jingle-cart-priority-actions">${selected ? `<span class="jingle-cart-added" tabindex="-1" data-service-status="${escapeHTML(service.key)}"><svg viewBox="0 0 20 20" aria-hidden="true"><path d="m4 10 4 4 8-8" fill="none" stroke="currentColor" stroke-width="2"/></svg>Added to your order</span><strong>${price(selected.final_line_price)}</strong><button type="button" class="jingle-cart-remove" data-cart-action data-service-remove="${escapeHTML(service.key)}" aria-label="Remove ${escapeHTML(service.title)}">Remove</button>` : `<strong class="jingle-cart-service-price">${price(service.price)}</strong><button type="button" class="jingle-cart-secondary" data-cart-action data-service-add="${escapeHTML(service.key)}" aria-label="Add ${escapeHTML(service.title)} for ${escapeHTML(price(service.price))}">Add</button>`}</div></section>`;
      }).join('')}</div>`);
    }
    const discounts = drawer.querySelector('[data-cart-discounts]');
    if (discounts) discounts.innerHTML = (cart.cart_level_discount_applications || []).map(discount => `<div><span>${escapeHTML(discount.title)}</span><strong>−${price(discount.total_allocated_amount)}</strong></div>`).join('');
    const total = drawer.querySelector('[data-cart-subtotal]');
    if (total) total.textContent = price(cart.total_price);
    if (footer) footer.hidden = false;
    if (busy) setBusy(true);
    syncCheckout();
  };
  const load = async () => {
    cartVerified = false;
    syncCheckout();
    const version = ++loadVersion;
    const fetched = await request(`${root}cart.js`);
    if (version !== loadVersion) return fetched;
    const cart = await cleanServices(fetched);
    if (version === loadVersion) render(cart);
    return cart;
  };
  const open = async trigger => {
    if (!drawer?.showModal) { location.assign(cartURL); return; }
    if (!drawer.open) {
      opener = trigger instanceof HTMLElement ? trigger : document.activeElement;
      drawer.showModal(); document.body.classList.add('jingle-cart-open');
      document.dispatchEvent(new Event('jingle:dialog-change'));
      drawer.querySelector('[data-cart-heading]')?.focus({ preventScroll: true });
    }
    showError('');
    if (busy) return;
    setBusy(true);
    try { await load(); } catch (error) {
      showError(error.message);
      if (!currentCart && body) body.innerHTML = `<div class="jingle-cart-empty"><p>You can continue in your full bag.</p><a class="jingle-cart-secondary" href="${escapeHTML(cartURL)}">View your bag</a></div>`;
    } finally { setBusy(false); }
  };
  const close = () => { if (drawer?.open) drawer.close(); };
  drawer?.addEventListener('close', () => {
    document.body.classList.remove('jingle-cart-open');
    document.dispatchEvent(new Event('jingle:dialog-change'));
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  });
  drawer?.addEventListener('click', event => {
    if (event.target !== drawer) return;
    const rect = drawer.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
  });
  const mutate = async (operation, message, focus) => {
    if (busy) return;
    ++loadVersion;
    cartVerified = false;
    setBusy(true); showError('');
    try {
      const cart = await operation();
      render(await cleanServices(cart || await request(`${root}cart.js`)));
      announce(message);
      setBusy(false);
      if (focus) focus();
    } catch (error) {
      showError(error.message); announce(error.message);
      // Read back the server state; never retry a potentially successful add.
      await load().catch(() => {});
      errorBox?.focus({ preventScroll: true });
    } finally { setBusy(false); }
  };
  const change = (key, quantity, selector) => {
    if (!Number.isSafeInteger(quantity) || quantity < 0) { load().catch(error => showError(error.message)); return; }
    mutate(() => request(endpoint(config.cartChangeUrl, 'cart/change.js'), { method: 'POST', body: JSON.stringify({ id: key, quantity }) }), quantity === 0 ? 'Item removed from your bag.' : 'Your bag has been updated.', () => {
      const row = [...body.querySelectorAll('[data-line-key]')].find(node => node.dataset.lineKey === key);
      (row?.querySelector(selector) || drawer.querySelector('[data-cart-heading]'))?.focus({ preventScroll: true });
    });
  };
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-cart-open]');
    if (trigger) { event.preventDefault(); open(trigger); return; }
    if (event.target.closest('[data-cart-refresh]')) { event.preventDefault(); if (!busy) open(); return; }
    if (event.target.closest('[data-cart-close]')) { close(); return; }
    if (!drawer?.contains(event.target) || busy) return;
    const upgradeButton = event.target.closest('[data-kit-upgrade]');
    if (upgradeButton) {
      mutate(async () => {
        const latest = await request(`${root}cart.js`);
        const upgrade = window.HwOffers?.findUpgrade(latest, kitOffers, config.currency, upgradeButton.dataset.kitUpgrade);
        if (!upgrade) throw new Error('Your bag changed. Please check the updated items below.');
        // One request replaces the single kit with the native two-kit variant.
        // Never add a second standalone kit or retry an uncertain mutation.
        const result = await request(`${root}cart/update.js`, { method: 'POST', body: JSON.stringify({ updates: { [upgrade.key]: 0, [String(upgrade.to)]: 1 } }) });
        upgraded = upgrade.to;
        return result;
      }, 'Added. Your bag now has two complete kits.', () => drawer.querySelector('[data-upgrade-confirm]')?.focus());
      return;
    }
    const serviceAdd = event.target.closest('[data-service-add]');
    if (serviceAdd) {
      const service = services.find(item => item.key === serviceAdd.dataset.serviceAdd);
      if (!service?.available || !currentCart || !hasPhysicalItems(currentCart) || currentCart.items.some(item => String(item.variant_id) === service.variantId)) return;
      mutate(async () => {
        await request(endpoint(config.cartAddUrl, 'cart/add.js'), { method: 'POST', body: JSON.stringify({ items: [{ id: Number(service.variantId), quantity: 1 }] }) });
      }, `${service.title} added to your order.`, () => drawer.querySelector(`[data-service-status="${service.key}"]`)?.focus());
      return;
    }
    const serviceRemove = event.target.closest('[data-service-remove]');
    if (serviceRemove) {
      const service = services.find(item => item.key === serviceRemove.dataset.serviceRemove);
      const item = currentCart?.items.find(line => String(line.variant_id) === service?.variantId);
      if (item) change(item.key, 0, '[data-cart-remove]');
      return;
    }
    const row = event.target.closest('[data-line-key]');
    if (!row) return;
    if (event.target.closest('[data-cart-remove]')) { change(row.dataset.lineKey, 0, '[data-cart-remove]'); return; }
    const step = event.target.closest('[data-cart-step]');
    if (step) {
      const item = currentCart.items.find(line => line.key === row.dataset.lineKey);
      const increment = item?.quantity_rule?.increment || 1;
      const quantity = Math.max(0, Number(row.querySelector('[data-cart-quantity]').value) + Number(step.dataset.cartStep) * increment);
      change(row.dataset.lineKey, quantity, `[data-cart-step="${step.dataset.cartStep}"]`);
    }
  });
  drawer?.addEventListener('change', event => {
    if (busy || !event.target.matches('[data-cart-quantity]')) return;
    change(event.target.closest('[data-line-key]').dataset.lineKey, Number(event.target.value), '[data-cart-quantity]');
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (form.matches('[data-full-service-form]')) {
      if (busy || cleanPromise) { event.preventDefault(); return; }
      setBusy(true);
      return;
    }
    if (form.matches('[data-full-cart-form]')) {
      if (busy || cleanPromise) { event.preventDefault(); return; }
      const hasItems = [...form.querySelectorAll('[data-main-quantity]')].some(input => Number(input.value) > 0);
      if (!hasItems) {
        form.querySelectorAll('[data-service-quantity]').forEach(input => { input.value = '0'; });
        if (event.submitter?.name === 'checkout') {
          event.preventDefault();
          form.requestSubmit(form.elements.namedItem('update'));
        }
      }
      return;
    }
    if (form.matches('[data-cart-checkout-form]')) {
      if (busy || !canCheckout()) { event.preventDefault(); if (!busy) open(); }
      return;
    }
    if (!form.matches('[data-product-form]') || !drawer?.showModal) return;
    event.preventDefault(); if (busy) return;
    const submitButton = event.submitter || form.querySelector('[type="submit"]');
    let data = new FormData(form);
    const id = Number(data.get('id'));
    const quantity = Number(data.get('quantity') || 1);
    const localError = form.querySelector('[data-product-error]') || form.parentElement.querySelector('[data-product-error]');
    if (localError) { localError.hidden = true; localError.textContent = ''; }
    if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(quantity) || quantity < 1) {
      if (localError) { localError.textContent = 'Please choose an available option and quantity.'; localError.hidden = false; }
      return;
    }
    
    ++loadVersion; cartVerified = false; setBusy(true);
    const label = submitButton?.querySelector('[data-add-label]');
    const originalLabel = label?.textContent;
    if (label) label.textContent = 'Adding…';
    await open(submitButton);
    if (body) body.innerHTML = '<div class="jingle-cart-empty"><h3>Adding to your bag…</h3><p>Just a moment while we confirm your selection</p></div>';
    if (footer) footer.hidden = true;
    announce('Adding to your bag.');
    let added = false;
    try {
      await request(endpoint(config.cartAddUrl, 'cart/add.js'), { method: 'POST', body: data });
      added = true;
      await load(); announce('Added to your bag.');
    } catch (error) {
      let recovered = false;
      // A timeout may happen after Shopify accepted the add. Read, never re-post.
      if (!added) {
        try { await load(); recovered = true; } catch { /* The full-bag link remains available below. */ }
      }
      const text = added ? 'Your item was added. Open your full bag to continue.' : error.uncertain && recovered ? 'Your current bag is shown below. Please check it before adding again.' : error.message;
      if (!recovered && body) body.innerHTML = `<div class="jingle-cart-empty"><p>Continue in your full bag</p><a class="jingle-cart-secondary" href="${escapeHTML(cartURL)}">View your bag</a></div>`;
      if (localError) { localError.textContent = text; localError.hidden = false; }
      if (drawer.open) showError(text);
      announce(text);
    } finally { setBusy(false); if (label) label.textContent = originalLabel; }
  });
  // Native full cart remains a usable fallback. Clean service-only carts on load.
  if (document.querySelector('[data-jingle-cart-page]') && services.length) {
    request(`${root}cart.js`).then(async cart => {
      const cleaned = await cleanServices(cart);
      if (cleaned !== cart) location.reload();
    }).catch(() => {});
  }
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    setBusy(false);
    if (drawer?.open) load().catch(error => showError(error.message));
  });
})();
